import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import type {
  AssetCharge,
  AssetChargePreview,
  AssetChargeQuery,
  CreatePurchaseCharge,
} from '../models/assets/asset-charges.dto';
import type { PaginatedListDto } from '../models/common/paginated-list.dto';

@Injectable({ providedIn: 'root' })
export class AssetChargesService {
  private readonly http = inject(HttpClient);

  private apiUrl(path: string): string {
    const base = environment.apiBaseUrl.replace(/\/+$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  }

  list(q: AssetChargeQuery = {}): Observable<PaginatedListDto<AssetCharge>> {
    let params = new HttpParams();
    if (q.userId) params = params.set('userId', q.userId);
    if (q.assetId) params = params.set('assetId', q.assetId);
    if (q.reason) params = params.set('reason', String(q.reason));
    if (q.status) params = params.set('status', String(q.status));
    if (q.pageNumber) params = params.set('pageNumber', String(q.pageNumber));
    if (q.pageSize) params = params.set('pageSize', String(q.pageSize));
    return this.http.get<PaginatedListDto<AssetCharge>>(this.apiUrl('/api/AssetCharges'), { params });
  }

  get(id: string): Observable<AssetCharge> {
    return this.http.get<AssetCharge>(this.apiUrl(`/api/AssetCharges/${encodeURIComponent(id)}`));
  }

  preview(userId: string, amount: number, installments: number): Observable<AssetChargePreview> {
    const params = new HttpParams().set('userId', userId).set('amount', String(amount)).set('installments', String(installments));
    return this.http.get<AssetChargePreview>(this.apiUrl('/api/AssetCharges/preview'), { params });
  }

  /** The current payroll period as `yyyy-MM`. */
  currentPeriod(): Observable<string> {
    return this.http.get(this.apiUrl('/api/AssetCharges/current-period'), { responseType: 'text' });
  }

  createPurchase(dto: CreatePurchaseCharge): Observable<AssetCharge> {
    return this.http.post<AssetCharge>(this.apiUrl('/api/AssetCharges/purchase'), dto);
  }

  waive(id: string, reason: string): Observable<AssetCharge> {
    return this.http.post<AssetCharge>(this.apiUrl(`/api/AssetCharges/${encodeURIComponent(id)}/waive`), { reason });
  }

  linkAsset(id: string, assetId: string): Observable<AssetCharge> {
    return this.http.put<AssetCharge>(this.apiUrl(`/api/AssetCharges/${encodeURIComponent(id)}/asset`), { assetId });
  }
}
